import React, { Fragment, useState, useMemo } from "react";
import { Divider, Grid, Typography } from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import StatusField from "components/StatusField";
import {
  Datagrid,
  Filter,
  TextField,
  SearchInput,
  FunctionField,
  ReferenceField,
  BulkDeleteButton,
  SelectInput,
  BulkDeleteButtonProps,
  ListProps,
  CreateButton,
  TopToolbar,
  useDataProvider,
  ListActionsProps,
  useNotify,
  Button as RAButton,
} from "react-admin";
import { statusList } from "./conts";
import { GridinRow, GridLabel } from "utils/style";
import CustomDateField from "components/CustomDateField";
import { SchoolIdSelected } from "utils/tools";
import CustomStatusField from "./components/CustomStatusField";
import InternalNoteParentButton from "./components/InternalNoteParentButton";
import fileDownload from "js-file-download";
import { Parser } from "json2csv";
import { useSelector } from "react-redux";
import { ReduxState } from "ra-core";
import DownloadIcon from "@material-ui/icons/GetApp";
import FinancialInformationField from "./components/FinancialInformationField";
import ListShowButton from "../../components/ListShowButton";
import ListEditButton from "../../components/ListEditButton";
import ListWrapper from "../../components/ListWrapper";
import ActionsMenu from "../../components/ActionsMenu";
import { availableOnlySuperAdmin } from "utils/validation";

const BulkActionButtons = (props: BulkDeleteButtonProps) => (
  <Fragment>
    <BulkDeleteButton {...props} />
  </Fragment>
);

const ParentFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="search" alwaysOn />
    <SelectInput label="Status" source="Status" choices={statusList} alwaysOn />
  </Filter>
);

const ParentList = (props: ListProps) => {
  const notify = useNotify();
  const { getRecordForCSV } = useDataProvider();
  const navMode = useSelector((state: ReduxState) => state.navMode);

  const ActionButtons = (_props: ListActionsProps) => {
    const [isCSVLoading, setCSVLoading] = useState(false);
    const [progress, setProgress] = useState<{
      current: number | null;
      total: number | null;
    }>({
      current: 0,
      total: 0,
    });

    const exportCSV = () => {
      if (isCSVLoading) return;
      // logic, first fetch all parents list
      // every page do request to MBC API proxy to get outstanding
      // do until all records loaded
      // put into csv format and then initiate download function
      getRecordForCSV("parents", {
        filter: { SchoolId: navMode.school.id },
        body: { school: navMode.school },
        callback: ({ isLoading, loadedRecordCount, totalRecords }: any) => {
          // Only update loading state once at start and end
          if (isLoading !== undefined) {
            setCSVLoading(isLoading);
          }

          setProgress({
            current: loadedRecordCount,
            total: totalRecords,
          });
        },
      })
        .then(({ data }: any) => {
          const parser = new Parser();
          const csvData = data.flat();
          fileDownload(
            parser.parse(csvData),
            `Parent Records - ${navMode.school.name}.csv`
          );
        })
        .catch(() => {
          notify("Failed to retrieve records, please try again", "warning");
        })
        .finally(() => {
          // Reset loading state and progress regardless of success or failure
          setCSVLoading(false);
          setProgress({
            current: null,
            total: null,
          });
        });
    };

    const downloadText = useMemo(() => {
      if (!isCSVLoading) {
        return "Export";
      }

      if (progress.current === null && progress.total === null) {
        return "Fetching Parents";
      }

      if (progress.current !== null && progress.total !== null) {
        return `Fetching MBC Data ${progress.current}/${progress.total}`;
      }

      return "Downloading CSV";
    }, [isCSVLoading, progress]);

    return (
      <TopToolbar>
        {availableOnlySuperAdmin() && (
          <RAButton onClick={exportCSV} label={downloadText}>
            <DownloadIcon />
          </RAButton>
        )}
        <CreateButton />
      </TopToolbar>
    );
  };

  const breadcrumb = [{ link: "/parents", title: "Parents" }];

  return (
    <>
      <ListWrapper
        breadcrumb={breadcrumb}
        headerTitle="Parents Management"
        exporter={false}
        filters={<ParentFilter />}
        filter={{
          SchoolId: Number(SchoolIdSelected()),
          IsHaveStudent: true,
          searchAllParent: true, // custom for data provider
        }}
        sort={{ field: "CreatedAt", order: "DESC" }}
        bulkActionButtons={<BulkActionButtons />}
        actions={<ActionButtons />}
        {...props}
      >
        <Datagrid>
          <FunctionField
            label="Parent Info"
            render={() => (
              <SanitizedGrid container spacing={2}>
                <Grid item xs={12} style={GridinRow}>
                  <Typography variant="body2" style={GridLabel}>
                    Id :
                  </Typography>
                  <TextField source="id" />
                </Grid>

                <Grid item xs={12} style={GridinRow}>
                  <Typography variant="body2" style={GridLabel}>
                    Name :
                  </Typography>
                  <FunctionField
                    render={(record: any) =>
                      `${record.firstName} ${record.lastName}`
                    }
                  />
                </Grid>

                <Grid item xs={12} style={GridinRow}>
                  <Typography variant="body2" style={GridLabel}>
                    Email :
                  </Typography>
                  <TextField source="user.email" emptyText="-" />
                </Grid>

                <Grid item xs={12} style={GridinRow}>
                  <Typography variant="body2" style={GridLabel}>
                    Mobile :
                  </Typography>
                  <TextField source="mobile" />
                </Grid>

                <Grid item xs={12} style={GridinRow}>
                  <Typography variant="body2" style={GridLabel}>
                    Mbc Code :
                  </Typography>
                  <TextField source="user.mbcCustomerId" />
                </Grid>
              </SanitizedGrid>
            )}
          />
          <FunctionField
            label="Student Info"
            render={(record: any) => (
              <>
                {record.students && (
                  <SanitizedGrid container spacing={2}>
                    {record.students.length > 0 ? (
                      <>
                        {record.students.map(
                          (student: IStudent, index: number) => {
                            return (
                              <Fragment key={`student-key-${index}`}>
                                <Grid item xs={12} style={GridinRow}>
                                  <Typography variant="body2">
                                    Id : {student.id}
                                  </Typography>
                                </Grid>

                                <Grid item xs={12} style={GridinRow}>
                                  <Typography variant="body2">
                                    Name : {student.firstName}{" "}
                                    {student.lastName}
                                  </Typography>
                                </Grid>

                                <Grid item xs={12} style={GridinRow}>
                                  <Typography variant="body2" style={GridLabel}>
                                    School :
                                  </Typography>
                                  <ReferenceField
                                    source={`students[${index}].schoolId`}
                                    reference="schools"
                                    emptyText="-"
                                    link={false}
                                  >
                                    <TextField source="name" />
                                  </ReferenceField>
                                </Grid>

                                <Grid item xs={4} style={GridinRow}>
                                  <Typography variant="body2" style={GridLabel}>
                                    Level :
                                  </Typography>
                                  <ReferenceField
                                    source={`students[${index}].levelId`}
                                    reference="levels"
                                    emptyText="-"
                                    link={false}
                                  >
                                    <TextField source="name" />
                                  </ReferenceField>
                                </Grid>

                                <Grid item xs={4} style={GridinRow}>
                                  <Typography variant="body2" style={GridLabel}>
                                    Class :
                                  </Typography>
                                  <ReferenceField
                                    source={`students[${index}].levelGroupId`}
                                    reference="levelGroups"
                                    emptyText="-"
                                    link={false}
                                  >
                                    <TextField source="name" />
                                  </ReferenceField>
                                </Grid>

                                <Grid item xs={12} style={GridinRow}>
                                  <CustomStatusField title={student.status} />
                                </Grid>

                                <Grid item xs={12} style={GridinRow}>
                                  {/*{student.isMain && (*/}
                                  {/*  <CustomStatusField title={"Main Student"} />*/}
                                  {/*)}*/}

                                  {!student.isMain && (
                                    <>
                                      {student.resource
                                        .split(",")
                                        .map(
                                          (resource: string, index: number) => (
                                            <CustomStatusField
                                              title={resource}
                                              key={`student-relation-status-key-${index}`}
                                            />
                                          )
                                        )}
                                    </>
                                  )}
                                </Grid>

                                {record.students.length - 1 !== index && (
                                  <Grid item xs={8}>
                                    <Divider />
                                  </Grid>
                                )}
                              </Fragment>
                            );
                          }
                        )}
                      </>
                    ) : (
                      <>
                        <Grid item xs={12}>
                          <Typography
                            variant="body2"
                            style={{ fontWeight: "bold", fontStyle: "italic" }}
                          >
                            No access to the students list.
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </SanitizedGrid>
                )}
              </>
            )}
          />
          <CustomDateField source="createdAt" emptyText="-" isDateTime />
          <ReferenceField
            source="user.mbcCustomerId"
            label="Outstanding Amount"
            reference="customerCards"
            emptyText="-"
            link={false}
          >
            <FunctionField
              render={(record: any) => (
                <FinancialInformationField
                  source="balanceLCY"
                  financialData={record}
                />
              )}
            />
          </ReferenceField>
          <StatusField source="status" />
          <FunctionField
            label="Actions"
            render={(record: any) => (
              <ActionsMenu
                render={[
                  <ListShowButton record={record} />,
                  <ListEditButton record={record} />,
                  <InternalNoteParentButton parentId={record.id} />,
                ]}
              />
            )}
          />
        </Datagrid>
      </ListWrapper>
    </>
  );
};

export default ParentList;
